import React, { useEffect, useState } from 'react';
import { Box, Collapse, Fade, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { colors, COLORS } from '@constants/colors';
import { ScreenSizes } from '@interfaces';
import { useMytaStreams } from './MytaStreamsProvider';
import {
  ADVISORY_AUTOHIDE_MS,
  URGENT_COLLAPSE_MS,
  describeNotice,
  noticeKey,
  type NoticeSeverity,
} from './spotDrain';

const ICONS: Record<NoticeSeverity, typeof WarningAmberIcon> = {
  info: InfoOutlinedIcon,
  warning: WarningAmberIcon,
  error: ErrorOutlineIcon,
};

// Spot per client (myta-streams 2026-09-08): everything AWS does to the GPU box behind this session is said out
// loud, over the stream, while it happens — the two-minute reclaim notice with its countdown, the move to a new
// machine, and a machine that is already gone. Renders nothing when there is nothing to say.
//
// It says it in one line. The first version was a filled MUI Alert three lines tall across the top of the world:
// it sat on the top-centre chrome row (over Editor Mode's branding bar), could not be folded away, and a
// rebalance recommendation — which asks nothing of anyone and may never come true — stayed there for the rest of
// the visit. So: a pill below the chrome row, collapsed to its one-line summary, click to read the rest;
// advisories carry a × and retire by themselves, urgent notices open once and then fold back on their own.
const SpotNoticeBanner = () => {
  const { drainNotice, spotNotice } = useMytaStreams();
  const [now, setNow] = useState(() => Date.now());
  const [expanded, setExpanded] = useState(false);
  // The user has had their say about this message — no auto-collapse and no auto-retire on top of it.
  const [pinned, setPinned] = useState(false);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!drainNotice || drainNotice.kind !== 'interrupt') {
      return undefined;
    }
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [drainNotice]);

  // A live interruption notice counts down every second; every other message is shown as the provider set it.
  const view = drainNotice ? describeNotice(drainNotice, now) : spotNotice;
  const key = view ? noticeKey(view) : null;
  const advisory = !!view?.advisory;

  // A new message starts over: urgent ones show their text once, advisories stay folded.
  useEffect(() => {
    if (!key) {
      return;
    }
    setPinned(false);
    setExpanded(!advisory);
  }, [key, advisory]);

  useEffect(() => {
    if (!key || !expanded || pinned) {
      return undefined;
    }
    const timer = setTimeout(() => setExpanded(false), URGENT_COLLAPSE_MS);
    return () => clearTimeout(timer);
  }, [key, expanded, pinned]);

  useEffect(() => {
    if (!key || !advisory || pinned) {
      return undefined;
    }
    const timer = setTimeout(() => setDismissedKey(key), ADVISORY_AUTOHIDE_MS);
    return () => clearTimeout(timer);
  }, [key, advisory, pinned]);

  if (!view || !key || dismissedKey === key) {
    return null;
  }

  const accent = view.severity === 'error' ? colors.red : COLORS.WARNING;
  const Icon = ICONS[view.severity];

  const toggle = () => {
    setPinned(true);
    setExpanded((open) => !open);
  };

  return (
    <Fade in appear timeout={220}>
      <Box
        sx={(theme) => ({
          position: 'fixed',
          // Below the top chrome row, not on it: Editor Mode's branding bar is top:32 + 56 tall and centred on
          // the same axis (EditorMode/styles.ts brandingBarWrap), and this used to land right on top of it.
          top: '96px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1400,
          width: 'max-content',
          maxWidth: 'min(460px, calc(100vw - 32px))',
          pointerEvents: 'none',
          [theme.breakpoints.down(ScreenSizes.TabletHorizontal)]: {
            top: '72px',
          },
        })}
      >
        <Box
          data-testid="myta-spot-notice"
          sx={{
            pointerEvents: 'auto',
            background: colors.black_99,
            backdropFilter: 'blur(12px)',
            border: `1px solid ${accent}`,
            borderRadius: expanded ? '16px' : '99px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
            color: COLORS.WHITE,
            overflow: 'hidden',
            transition: 'border-radius 0.2s ease',
          }}
        >
          <Box
            onClick={toggle}
            role="button"
            aria-expanded={expanded}
            data-testid="myta-spot-notice-summary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: expanded ? '10px 8px 6px 14px' : '5px 8px 5px 14px',
              cursor: 'pointer',
              minWidth: 0,
            }}
          >
            <Icon sx={{ fontSize: 18, color: accent, flexShrink: 0 }} />
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {view.short}
            </Typography>
            {view.secondsLeft !== undefined ? (
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: accent,
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {view.secondsLeft} s
              </Typography>
            ) : null}
            {expanded ? (
              <ExpandLessIcon
                sx={{ fontSize: 18, color: colors.white_60, flexShrink: 0 }}
              />
            ) : (
              <ExpandMoreIcon
                sx={{ fontSize: 18, color: colors.white_60, flexShrink: 0 }}
              />
            )}
            {/* Only an advisory can be waved away — a machine that is actually going away is not the user's
                choice to un-know, and it retires by itself when the move is over. */}
            {advisory ? (
              <IconButton
                size="small"
                aria-label="Dismiss"
                data-testid="myta-spot-notice-dismiss"
                onClick={(event) => {
                  event.stopPropagation();
                  setDismissedKey(key);
                }}
                sx={{ color: colors.white_60, padding: '2px', flexShrink: 0 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
          </Box>
          <Collapse in={expanded} timeout={180} unmountOnExit>
            <Typography
              sx={{
                fontSize: '12.5px',
                lineHeight: 1.45,
                color: colors.white_80,
                padding: '0 14px 12px',
              }}
            >
              {view.body}
            </Typography>
          </Collapse>
        </Box>
      </Box>
    </Fade>
  );
};

export default SpotNoticeBanner;
